import React, { useState } from "react";
import { Star, Quote, CheckCircle, Sparkles } from "lucide-react";

const ReviewCard = ({ review }) => {
  const [isHovered, setIsHovered] = useState(false);

  // --- Helpers ---
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return isNaN(date.getTime())
      ? ""
      : date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
  };

  const getInitials = (name) => {
    return name
      .split(" ")
      .slice(0, 2)
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  const formattedDate = review.created_at ? formatDate(review.created_at) : "";
  const reviewerName = review.name || "Anonymous";
  const rating = review.rating || 5;

  // --- Render Stars with Animation ---
  const renderStars = () => {
    return (
      <div className="flex gap-1">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="relative">
            <Star
              size={14}
              fill={i < Math.floor(rating) ? "#D4AF37" : "#E5E7EB"} // Solid Gold or Gray
              strokeWidth={0} // No border for cleaner look
              className={`transition-transform duration-300 ${
                isHovered && i < rating ? "scale-110" : "scale-100"
              }`}
            />
            {/* Subtle Glow Effect behind active stars */}
            {i < rating && isHovered && (
              <div className="absolute inset-0 bg-[#D4AF37] blur-[4px] opacity-40 rounded-full" />
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div
      className={`group relative flex flex-col h-full bg-white rounded-2xl overflow-hidden border transition-all duration-500 ${
        isHovered
          ? "border-[#D4AF37]/40 shadow-[0_10px_40px_-10px_rgba(212,175,55,0.15)] -translate-y-1"
          : "border-gray-100 shadow-sm"
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* ==============================================
          HEADER SECTION (Image or Pattern)
      =============================================== */}
      <div className="relative w-full h-48 overflow-hidden bg-gray-50">
        {review.image ? (
          // 1. Product Image Exists
          <>
            <img
              src={review.image}
              alt="Review Product"
              className={`w-full h-full object-cover transition-transform duration-700 ease-out ${
                isHovered ? "scale-110" : "scale-100"
              }`}
            />
            <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors duration-500" />
          </>
        ) : (
          // 2. No Image -> "Premium Pattern" Fallback
          // This prevents the card from looking empty. It uses CSS radial gradients.
          <div className="w-full h-full relative bg-[#FAFAFA]">
            {/* Abstract Decorative Circle */}
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full border-[1px] border-[#D4AF37]/20" />
            <div className="absolute top-10 -left-10 w-32 h-32 rounded-full border-[1px] border-[#D4AF37]/10" />
            
            {/* Center Text/Icon for fallback */}
            <div className="absolute inset-0 flex flex-col items-center justify-center text-[#D4AF37]/80">
              <Sparkles className="w-8 h-8 mb-2 opacity-50" />
              <span 
                className="text-lg tracking-widest opacity-60"
                style={{ fontFamily: "Losta Masta, serif" }}
              >
                MASHO
              </span>
            </div>
          </div>
        )}

        {/* Product Name Badge (Floating) */}
        {review.product_name && (
          <div className="absolute bottom-3 left-3 right-3">
             <div className="bg-white/95 backdrop-blur-sm border border-[#D4AF37]/20 px-4 py-2 rounded-lg shadow-sm">
                <p 
                  className="text-gray-900 text-xs font-medium tracking-wide truncate"
                  style={{ fontFamily: "Montserrat, serif" }}
                >
                  {review.product_name}
                </p>
             </div>
          </div>
        )}
      </div>

      {/* ==============================================
          CONTENT BODY
      =============================================== */}
      <div className="flex flex-col flex-grow p-5 relative">
        
        {/* User Info Row */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            {/* Avatar - Monogram Style */}
            <div className={`relative w-10 h-10 rounded-full flex items-center justify-center border transition-colors duration-300 ${
                isHovered ? "border-[#D4AF37] bg-[#D4AF37]" : "border-[#D4AF37]/30 bg-white"
            }`}>
               <span 
                 className={`text-sm font-bold transition-colors duration-300 ${isHovered ? "text-white" : "text-[#D4AF37]"}`}
                 style={{ fontFamily: "Montserrat, serif" }}
               >
                 {getInitials(reviewerName)}
               </span>
               
               {/* Verified Checkmark */}
               {review.verified !== false && (
                 <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-[2px] shadow-sm">
                   <CheckCircle size={12} className="text-[#D4AF37] fill-white" />
                 </div>
               )}
            </div>

            {/* Name */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 leading-tight" style={{ fontFamily: "Montserrat, serif" }}>
                {reviewerName}
              </h4>
              <p className="text-[10px] text-gray-400 mt-0.5" style={{ fontFamily: "Montserrat, serif" }}>
                {formattedDate}
              </p>
            </div>
          </div>

          {/* Stars */}
          {renderStars()}
        </div>

        {/* Quote / Comment */}
        <div className="relative flex-grow">
          <Quote 
            className={`absolute -top-1 -left-1 w-6 h-6 text-[#D4AF37]/20 transition-all duration-300 ${
              isHovered ? "scale-110 opacity-100" : "opacity-50"
            }`} 
          />
          <p 
            className="text-gray-600 text-sm leading-relaxed pl-6 relative z-10"
            style={{ fontFamily: "Montserrat, serif" }}
          >
            {review.comment}
          </p>
        </div>

        {/* Decorative Gold Line at Bottom */}
        <div className={`absolute bottom-0 left-0 h-[2px] bg-[#D4AF37] transition-all duration-500 ease-in-out ${
           isHovered ? "w-full opacity-100" : "w-0 opacity-0"
        }`} />
      </div>
    </div>
  );
};

export default ReviewCard;